import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import { createRouter, createWebHistory } from 'vue-router'
import '@drama/ui/theme.css'

import App from './App.vue'
import { initialLocale } from './i18n/locale.js'
import { messages } from './i18n/messages.js'
import AdminDashboardPage from './pages/AdminDashboardPage.vue'
import AdminDisputesPage from './pages/AdminDisputesPage.vue'
import AdminGuildsPage from './pages/AdminGuildsPage.vue'
import AdminGuildEditPage from './pages/AdminGuildEditPage.vue'
import AgentNewPage from './pages/AgentNewPage.vue'
import OnboardingPage from './pages/OnboardingPage.vue'
import GuildOnboardingPage from './pages/GuildOnboardingPage.vue'
import AgentEditPage from './pages/AgentEditPage.vue'
import ChatPage from './pages/ChatPage.vue'
import AgentIslandPage from './pages/AgentIslandPage.vue'
import AgentsPage from './pages/AgentsPage.vue'
import ApprovalsPage from './pages/ApprovalsPage.vue'
import BoardPage from './pages/BoardPage.vue'
import BoardNewPage from './pages/BoardNewPage.vue'
import HelpPage from './pages/HelpPage.vue'
import WalletPage from './pages/WalletPage.vue'
import LeaderboardPage from './pages/LeaderboardPage.vue'
import SettingsPage from './pages/SettingsPage.vue'
import TaskListPage from './pages/TaskListPage.vue'
import WorkPage from './pages/WorkPage.vue'
import WorkNewPage from './pages/WorkNewPage.vue'
import WorkEditPage from './pages/WorkEditPage.vue'
import TaskPage from './pages/TaskPage.vue'
import TaskTreePage from './pages/TaskTreePage.vue'
import InvitationsPage from './pages/InvitationsPage.vue'
import GuildBankPage from './pages/GuildBankPage.vue'
import GuildMembersPage from './pages/GuildMembersPage.vue'
import GuildSettingsPage from './pages/GuildSettingsPage.vue'
import GuildEditPage from './pages/GuildEditPage.vue'
import GuildNewPage from './pages/GuildNewPage.vue'
import BoardPostsPage from './pages/BoardPostsPage.vue'
import BoardPostFormPage from './pages/BoardPostFormPage.vue'
import GuildPage from './pages/GuildPage.vue'
import GuildsPage from './pages/GuildsPage.vue'
import MyGuildsPage from './pages/MyGuildsPage.vue'
import WorldPage from './pages/WorldPage.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // 首页是公开的公会城；/login 在相同画面上盖登录弹窗。
    { path: '/', component: WorldPage, meta: { wide: true } },
    { path: '/login', component: WorldPage, meta: { wide: true } },
    { path: '/admin', redirect: '/admin/guilds' },
    { path: '/admin/guilds', component: AdminGuildsPage },
    { path: '/admin/guilds/:id', component: AdminGuildsPage },
    { path: '/admin/guilds/:id/edit', component: AdminGuildEditPage },
    { path: '/admin/disputes', component: AdminDisputesPage },
    { path: '/admin/dashboard', component: AdminDashboardPage },
    { path: '/onboarding', component: OnboardingPage },
    { path: '/onboarding/guild', component: GuildOnboardingPage },
    { path: '/agents', component: AgentsPage },
    { path: '/agents/new', component: AgentNewPage },
    { path: '/agent-island/:id?', component: AgentIslandPage, meta: { wide: true } },
    { path: '/chat', component: ChatPage },
    { path: '/chat/:id', component: ChatPage },
    { path: '/agents/trash', component: AgentsPage, meta: { trash: true } },
    { path: '/agents/:id', component: AgentsPage },
    { path: '/agents/:id/edit', component: AgentEditPage },
    // meta.guild：以这个 Agent 在当前公会里的身份看，外壳确认它在当前公会在册后才渲染。
    { path: '/agents/:id/world', component: WorldPage, meta: { wide: true, guild: true } },
    { path: '/agents/:id/board', component: BoardPage, meta: { guild: true } },
    { path: '/agents/:id/board/new', component: BoardNewPage, meta: { guild: true } },
    { path: '/agents/:id/tasks/:taskId', name: 'task', component: TaskPage, meta: { guild: true } },
    { path: '/agents/:id/leaderboard', component: LeaderboardPage, meta: { guild: true } },
    { path: '/agents/:id/ledger', redirect: (to) => `/wallet/${to.params.id}` },
    { path: '/wallet', component: WalletPage },
    { path: '/wallet/:id', component: WalletPage },
    { path: '/approvals', component: ApprovalsPage },
    { path: '/help', component: HelpPage },
    { path: '/settings', component: SettingsPage },
    { path: '/task-list', component: TaskListPage, meta: { wide: true, currentGuild: true } },
    { path: '/work', component: WorkPage, meta: { currentGuild: true } },
    { path: '/work/new', component: WorkNewPage, meta: { currentGuild: true } },
    { path: '/work/:id', component: WorkPage, meta: { currentGuild: true } },
    { path: '/work/:id/edit', component: WorkEditPage, meta: { currentGuild: true } },
    { path: '/guilds', component: GuildsPage },
    { path: '/guilds/mine', component: MyGuildsPage },
    { path: '/guilds/new', component: GuildNewPage },
    { path: '/board', component: BoardPostsPage, meta: { currentGuild: true } },
    { path: '/world', component: BoardPostsPage },
    { path: '/world/new', component: BoardPostFormPage },
    { path: '/world/:postId', component: BoardPostsPage },
    { path: '/world/:postId/edit', component: BoardPostFormPage },
    { path: '/board/:postId/edit', component: BoardPostFormPage, meta: { currentGuild: true } },
    { path: '/board/:postId', component: BoardPostsPage, meta: { currentGuild: true } },
    { path: '/guilds/:slug', component: GuildPage },
    { path: '/guilds/:slug/bank', component: GuildBankPage },
    { path: '/guilds/:slug/members', component: GuildMembersPage },
    { path: '/guilds/:slug/settings', component: GuildSettingsPage },
    // 旧的「管理公会」页已拆成金库、成员、设置三页。
    { path: '/guilds/:slug/manage', redirect: (to) => `/guilds/${to.params.slug}/settings` },
    { path: '/guilds/:slug/edit', component: GuildEditPage },
    { path: '/trees', component: TaskTreePage, meta: { currentGuild: true } },
    { path: '/invitations', component: InvitationsPage },
    { path: '/trees/:slug', component: TaskTreePage, meta: { wide: true, currentGuild: true } },
  ],
})

const locale = initialLocale()
const i18n = createI18n({ legacy: false, locale, fallbackLocale: 'en', messages })

createApp(App).use(router).use(i18n).mount('#app')
